(() => {
  if (window.saCartPromotionUpsell) {
    window.saCartPromotionUpsell.initialize(document);
    return;
  }

  const ROOT_SELECTOR = '[data-sa-cart-promotion]';
  const CANDIDATE_SELECTOR = 'template[data-sa-promotion-candidate]';

  const GROUPS = {
    'flip-lighter-tin': {
      label: 'flip lighter tins',
      itemSingular: 'tin case',
      itemPlural: 'tin cases',
      finalThreshold: 4,
      firstThreshold: 2,
      firstDiscount: '$5',
      viewMoreUrl: '/collections/all-products-lighters',
      viewMoreLabel: 'View more flip lighters',
    },
    'flip-lighter-only': {
      label: 'flip lighters',
      itemSingular: 'lighter',
      itemPlural: 'lighters',
      finalThreshold: 4,
      firstThreshold: 2,
      firstDiscount: '$5',
      viewMoreUrl: '/collections/all-products-lighters',
      viewMoreLabel: 'View more flip lighters',
    },
    'flip-lighter-tin-set': {
      label: 'flip lighter and tin sets',
      itemSingular: 'lighter and tin set',
      itemPlural: 'lighter and tin sets',
      finalThreshold: 4,
      firstThreshold: 2,
      firstDiscount: '$5',
      viewMoreUrl: '/collections/all-products-lighters',
      viewMoreLabel: 'View more flip lighters',
    },
    'stash-jar': {
      label: 'stash jars',
      itemSingular: 'stash jar',
      itemPlural: 'stash jars',
      finalThreshold: 4,
      firstThreshold: 2,
      firstDiscount: '$5',
      viewMoreUrl: '/collections/all-products-stash-jars',
      viewMoreLabel: 'View more stash jars',
    },
    'grinder-only': {
      label: 'grinders',
      itemSingular: 'grinder',
      itemPlural: 'grinders',
      finalThreshold: 3,
      viewMoreUrl: '/collections/herb-grinders',
      viewMoreLabel: 'View more grinder sets',
    },
    'rolling-tray': {
      label: 'rolling trays',
      itemSingular: 'rolling tray',
      itemPlural: 'rolling trays',
      finalThreshold: 3,
      viewMoreUrl: '/collections/all-rolling-trays',
      viewMoreLabel: 'View more rolling trays',
    },
    'ashtray-only': {
      label: 'ashtrays',
      itemSingular: 'ashtray',
      itemPlural: 'ashtrays',
      finalThreshold: 3,
      viewMoreUrl: '/collections/all-products-ashtray',
      viewMoreLabel: 'View more ashtrays',
    },
    tapestry: {
      label: 'tapestries',
      itemSingular: 'tapestry',
      itemPlural: 'tapestries',
      finalThreshold: 4,
      firstThreshold: 2,
      firstDiscount: '$15',
      viewMoreUrl: '/collections/all-tapestries',
      viewMoreLabel: 'View more tapestries',
    },
    'grinder-set': {
      label: 'full grinder sets',
      itemSingular: 'full grinder set',
      itemPlural: 'full grinder sets',
      finalThreshold: 4,
      firstThreshold: 2,
      firstDiscount: '$10',
      viewMoreUrl: '/collections/herb-grinders',
      viewMoreLabel: 'View more grinder sets',
    },
    'stash-box-full': {
      label: 'full stashbox sets',
      itemSingular: 'full grinder set',
      itemPlural: 'full grinder sets',
      finalThreshold: 1,
      viewMoreUrl: '/collections/herb-grinders',
      viewMoreLabel: 'View more grinder sets',
      tierType: 'companion-discount',
      companionDiscount: '$10',
    },
    bag: {
      label: 'tote and sling bags',
      itemSingular: 'bag',
      itemPlural: 'bags',
      finalThreshold: 5,
      firstThreshold: 2,
      firstDiscount: '$10',
      midThreshold: 3,
      midDiscount: '$20',
      viewMoreUrl: '/collections/all-tote-bags',
      viewMoreLabel: 'View more tote and sling bags',
    },
  };

  class SaCartPromotionController {
    constructor(root) {
      this.root = root;
      this.dialog = root.querySelector('[data-sa-promotion-dialog]');
      this.items = root.querySelector('[data-sa-promotion-items]');
      this.status = root.querySelector('[data-sa-promotion-status]');
      this.emptyState = root.querySelector('[data-sa-promotion-empty]');
      this.recommendations = root.querySelector('[data-sa-promotion-recommendations]');
      this.maxRecommendations = Math.max(1, Number(root.dataset.maxRecommendations) || 6);
      this.preferSameSeries = root.dataset.preferSameSeries !== 'false';
      this.randomFallback = root.dataset.randomFallback !== 'false';
      this.lastTriggerSignature = '';
      this.previousFocus = null;
      this.activeGroupKey = '';
      this.activeTriggerCandidate = null;
      this.pending = false;
      this.inertSiblings = [];
      this.hasSwipeChoices = false;

      this.candidates = this.readCandidates();
      this.variantCandidates = this.indexCandidateVariants();

      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleRecommendationsScroll = this.handleRecommendationsScroll.bind(this);
      this.root.addEventListener('click', this.handleClick);
      this.root.addEventListener('keydown', this.handleKeydown);
      this.items.addEventListener('scroll', this.handleRecommendationsScroll, { passive: true });
    }

    destroy() {
      this.root.removeEventListener('click', this.handleClick);
      this.root.removeEventListener('keydown', this.handleKeydown);
      this.items.removeEventListener('scroll', this.handleRecommendationsScroll);
    }

    readCandidates() {
      return Array.from(this.root.querySelectorAll(CANDIDATE_SELECTOR)).map(template => ({
        template,
        triggerGroup: template.dataset.triggerGroup || '',
        recommendationGroup: template.dataset.recommendationGroup || '',
        productId: String(template.dataset.productId || ''),
        variantId: String(template.dataset.variantId || ''),
        qualifyingVariantIds: String(template.dataset.qualifyingVariantIds || '')
          .split('|')
          .filter(Boolean),
        series: template.dataset.series || '',
      }));
    }

    indexCandidateVariants() {
      const index = new Map();
      this.candidates.forEach(candidate => {
        candidate.qualifyingVariantIds.forEach(variantId => {
          const key = String(variantId);
          const candidates = index.get(key) || [];
          candidates.push(candidate);
          index.set(key, candidates);
        });
      });
      return index;
    }

    async handleSuccessfulAdd(event) {
      if (!event || event.source === 'sa-cart-promotion-upsell' || this.pending) return;

      const variantIds = this.getEventVariantIds(event);
      const triggerCandidate = variantIds
        .flatMap(variantId => this.variantCandidates.get(String(variantId)) || [])
        .filter(candidate => candidate.triggerGroup && GROUPS[candidate.triggerGroup])
        .find(Boolean);

      if (!triggerCandidate || !GROUPS[triggerCandidate.triggerGroup]) return;

      try {
        const cart = await this.fetchCart();
        if (!this.root.isConnected) return;

        const triggerStillPresent = cart.items.some(item => (
          variantIds.includes(String(item.variant_id ?? item.id)) && Number(item.quantity) > 0
        ));
        if (!triggerStillPresent) return;

        const quantity = this.getGroupQuantity(cart, triggerCandidate.triggerGroup);
        const group = GROUPS[triggerCandidate.triggerGroup];
        const addedQuantity = this.getAddedGroupQuantity(event, triggerCandidate.triggerGroup, variantIds);
        const previousQuantity = Math.max(0, quantity - addedQuantity);
        if (quantity <= 0 || (group.finalThreshold && quantity > group.finalThreshold && previousQuantity >= group.finalThreshold)) return;

        const signature = this.buildTriggerSignature(triggerCandidate.triggerGroup, variantIds, cart);
        if (signature === this.lastTriggerSignature) return;
        this.lastTriggerSignature = signature;

        this.activeGroupKey = triggerCandidate.triggerGroup;
        this.activeTriggerCandidate = triggerCandidate;
        this.renderState(cart, triggerCandidate.triggerGroup, triggerCandidate);
        this.open();
      } catch (error) {
        // The cart drawer remains the source of truth if cart verification is unavailable.
      }
    }

    getEventVariantIds(event) {
      const values = Array.isArray(event.productVariantIds)
        ? event.productVariantIds
        : [event.productVariantId];
      return values.map(value => String(value || '')).filter(Boolean);
    }

    buildTriggerSignature(groupKey, variantIds, cart) {
      const cartSignature = cart.items
        .map(item => `${item.key || item.variant_id}:${item.quantity}`)
        .sort()
        .join('|');
      return `${groupKey}:${variantIds.join(',')}:${cartSignature}`;
    }

    getGroupQuantity(cart, groupKey) {
      return cart.items.reduce((total, item) => {
        const candidates = this.variantCandidates.get(String(item.variant_id ?? item.id)) || [];
        return candidates.some(candidate => candidate.triggerGroup === groupKey)
          ? total + Number(item.quantity || 0)
          : total;
      }, 0);
    }

    getAddedGroupQuantity(event, groupKey, fallbackVariantIds) {
      const cartData = event.cartData || {};
      const addedItems = Array.isArray(cartData.items) ? cartData.items : [cartData];
      const quantity = addedItems.reduce((total, item) => {
        const variantId = String(item.variant_id ?? item.id ?? '');
        const candidates = this.variantCandidates.get(variantId) || [];
        return candidates.some(candidate => candidate.triggerGroup === groupKey)
          ? total + Number(item.quantity || 0)
          : total;
      }, 0);

      if (quantity > 0) return quantity;
      return fallbackVariantIds.reduce((total, variantId) => {
        const candidates = this.variantCandidates.get(String(variantId)) || [];
        return candidates.some(candidate => candidate.triggerGroup === groupKey) ? total + 1 : total;
      }, 0);
    }

    renderState(cart, groupKey, triggerCandidate) {
      const group = GROUPS[groupKey];
      if (!group) return;

      const quantity = this.getGroupQuantity(cart, groupKey);
      const state = this.getProgressState(group, quantity);
      const title = this.root.querySelector('[data-sa-promotion-title]');
      const message = this.root.querySelector('[data-sa-promotion-message]');
      const quantityLabel = this.root.querySelector('[data-sa-promotion-quantity]');
      const progress = this.root.querySelector('[data-sa-promotion-progress]');
      const viewMore = this.root.querySelector('[data-sa-promotion-view-more]');

      if (title) title.textContent = state.title;
      if (message) message.textContent = state.message;
      if (quantityLabel) quantityLabel.textContent = state.quantityLabel;
      if (progress) progress.style.width = `${state.progress}%`;
      if (quantityLabel) quantityLabel.hidden = !state.quantityLabel;
      if (progress) progress.parentElement.hidden = state.hideProgress === true;
      if (viewMore) {
        viewMore.href = group.viewMoreUrl;
        viewMore.textContent = group.viewMoreLabel;
      }

      this.recommendations.hidden = state.complete;
      if (!state.complete) this.renderRecommendations(cart, groupKey, triggerCandidate);
    }

    getProgressState(group, quantity) {
      if (group.tierType === 'companion-discount') {
        return {
          complete: false,
          title: `Save ${group.companionDiscount}`,
          message: `Add a ${group.itemSingular} for ${group.companionDiscount} off.`,
          quantityLabel: '',
          progress: 0,
          hideProgress: true,
        };
      }

      if (quantity >= group.finalThreshold) {
        return {
          complete: true,
          title: 'Congratulations',
          message: `Congrats! Your free ${group.itemSingular} offer is ready.`,
          quantityLabel: `${quantity} of ${group.finalThreshold} ${group.itemPlural}`,
          progress: 100,
        };
      }

      if (group.firstThreshold && quantity < group.firstThreshold) {
        const remaining = group.firstThreshold - quantity;
        return {
          complete: false,
          title: `Save ${group.firstDiscount}`,
          message: `Add ${remaining} more ${remaining === 1 ? group.itemSingular : group.itemPlural} and save ${group.firstDiscount}.`,
          quantityLabel: `${quantity} of ${group.firstThreshold} ${group.itemPlural}`,
          progress: Math.min(100, (quantity / group.firstThreshold) * 100),
        };
      }

      if (group.midThreshold && quantity < group.midThreshold) {
        const remaining = group.midThreshold - quantity;
        return {
          complete: false,
          title: `Save ${group.midDiscount}`,
          message: `Add ${remaining} more ${remaining === 1 ? group.itemSingular : group.itemPlural} and save ${group.midDiscount}.`,
          quantityLabel: `${quantity} of ${group.midThreshold} ${group.itemPlural}`,
          progress: Math.min(100, (quantity / group.midThreshold) * 100),
        };
      }

      const remaining = group.finalThreshold - quantity;
      const isOneAway = remaining === 1;
      return {
        complete: false,
        title: `Unlock a free ${group.itemSingular}`,
        message: isOneAway
          ? `Add a ${group.itemSingular} for free.`
          : `Buy ${group.finalThreshold - 1} ${group.itemPlural}, add a ${this.ordinal(group.finalThreshold)} ${group.itemSingular} for free.`,
        quantityLabel: `${quantity} of ${group.finalThreshold} ${group.itemPlural}`,
        progress: Math.min(100, (quantity / group.finalThreshold) * 100),
      };
    }

    ordinal(value) {
      const remainder = value % 100;
      if (remainder >= 11 && remainder <= 13) return `${value}th`;

      switch (value % 10) {
        case 1:
          return `${value}st`;
        case 2:
          return `${value}nd`;
        case 3:
          return `${value}rd`;
        default:
          return `${value}th`;
      }
    }

    renderRecommendations(cart, groupKey, triggerCandidate) {
      const cartProductIds = new Set(cart.items.map(item => String(item.product_id || '')));
      const eligible = this.candidates.filter(candidate => (
        candidate.recommendationGroup === groupKey
        && candidate.productId !== triggerCandidate.productId
        && !cartProductIds.has(candidate.productId)
      ));

      let sameSeries = [];
      let otherCandidates = eligible;
      if (this.preferSameSeries && triggerCandidate.series) {
        sameSeries = eligible.filter(candidate => candidate.series === triggerCandidate.series);
        otherCandidates = eligible.filter(candidate => candidate.series !== triggerCandidate.series);
      }

      const selected = sameSeries.slice(0, this.maxRecommendations);
      const remainingSlots = this.maxRecommendations - selected.length;
      if (remainingSlots > 0 && this.randomFallback) {
        const fallbackPool = sameSeries.length === 0
          ? this.shuffle(otherCandidates)
          : otherCandidates;
        selected.push(...fallbackPool.slice(0, remainingSlots));
      }

      this.items.replaceChildren();
      this.items.scrollLeft = 0;
      selected.forEach(candidate => {
        this.items.append(candidate.template.content.cloneNode(true));
      });

      this.emptyState.hidden = selected.length > 0;
      this.hasSwipeChoices = selected.length > 2;
      requestAnimationFrame(() => this.updateSwipeHint());
    }

    handleRecommendationsScroll() {
      this.updateSwipeHint();
    }

    updateSwipeHint() {
      const swipeHint = this.root.querySelector('[data-sa-promotion-swipe-hint]');
      if (!swipeHint) return;
      const atEnd = this.items.scrollLeft + this.items.clientWidth >= this.items.scrollWidth - 4;
      swipeHint.hidden = !this.hasSwipeChoices || atEnd;
    }

    shuffle(candidates) {
      const shuffled = [...candidates];
      for (let index = shuffled.length - 1; index > 0; index -= 1) {
        const randomIndex = Math.floor(Math.random() * (index + 1));
        [shuffled[index], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[index]];
      }
      return shuffled;
    }

    open() {
      if (!this.root.hidden) return;
      this.previousFocus = document.activeElement;
      if (this.status) {
        this.status.hidden = true;
        this.status.textContent = '';
        this.status.removeAttribute('data-state');
      }
      this.root.hidden = false;
      this.root.dataset.open = 'true';
      this.setBackgroundInert(true);
      this.activateDialogFocusTrap();
    }

    close() {
      if (this.root.hidden) return;
      this.root.hidden = true;
      delete this.root.dataset.open;
      this.setBackgroundInert(false);

      if (typeof removeTrapFocus === 'function') removeTrapFocus();
      const drawer = document.querySelector('cart-drawer');
      const drawerClose = drawer?.querySelector('[data-drawer-close]');
      if (drawer?.classList.contains('is-visible') && drawerClose) {
        if (typeof trapFocus === 'function') {
          trapFocus(drawer.firstElementChild, drawerClose);
        } else {
          drawerClose.focus();
        }
      } else if (this.previousFocus?.isConnected) {
        this.previousFocus.focus();
      }
    }

    activateDialogFocusTrap() {
      const focusDialog = () => {
        if (this.root.hidden) return;
        const closeButton = this.root.querySelector('.sa-cart-promotion__close');
        if (typeof trapFocus === 'function') {
          trapFocus(this.dialog, closeButton || this.dialog);
        } else {
          closeButton?.focus();
        }
      };

      const drawerInner = document.querySelector('cart-drawer .cart-drawer__inner');
      drawerInner?.addEventListener('transitionend', focusDialog, { once: true });
      requestAnimationFrame(focusDialog);
      window.setTimeout(focusDialog, 400);
    }

    setBackgroundInert(shouldBeInert) {
      if (shouldBeInert) {
        this.inertSiblings = Array.from(this.root.parentElement?.children || [])
          .filter(element => element !== this.root && !element.hasAttribute('inert'));
        this.inertSiblings.forEach(element => element.setAttribute('inert', ''));
        return;
      }

      this.inertSiblings.forEach(element => element.removeAttribute('inert'));
      this.inertSiblings = [];
    }

    handleClick(event) {
      event.stopPropagation();
      const closeControl = event.target.closest('[data-sa-promotion-close]');
      if (closeControl) {
        event.preventDefault();
        this.close();
        return;
      }

      const addButton = event.target.closest('[data-sa-promotion-add]');
      if (addButton) {
        event.preventDefault();
        this.addRecommendation(addButton);
      }
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.close();
        return;
      }

    }

    async addRecommendation(button) {
      if (this.pending || !this.activeGroupKey) return;
      const variantId = String(button.dataset.variantId || '');
      const candidates = this.variantCandidates.get(variantId) || [];
      if (!variantId || !candidates.some(candidate => candidate.recommendationGroup === this.activeGroupKey)) return;

      const drawer = document.querySelector('cart-drawer');
      const sections = drawer?.getSectionsToRender?.() || [];
      this.pending = true;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.querySelector('.loading__spinner')?.classList.remove('hidden');
      this.announce('Adding item to cart.', 'loading');

      try {
        const response = await fetch(routes.cart_add_url, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1 }],
            sections: sections.map(section => section.section),
            sections_url: window.location.pathname,
          }),
        });
        const responseData = await this.parseJson(response);
        if (!response.ok || responseData?.status || responseData?.errors) {
          throw new Error(responseData?.description || 'Item could not be added.');
        }

        drawer?.renderContents?.(responseData);
        this.activateDialogFocusTrap();
        const cart = await this.fetchCart();
        if (typeof syncCartAfterMutation === 'function') syncCartAfterMutation(cart);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'sa-cart-promotion-upsell',
            productVariantId: variantId,
            cartData: responseData,
          });
        }

        button.dataset.state = 'success';
        button.querySelector('[data-sa-promotion-add-label]').textContent = '\u2713';
        this.announce('Item added to cart. Promotion progress updated.', 'success');
        this.renderState(cart, this.activeGroupKey, this.activeTriggerCandidate);
      } catch (error) {
        button.disabled = false;
        this.announce(error.message || 'Item could not be added. Please try again.', 'error');
      } finally {
        this.pending = false;
        button.removeAttribute('aria-busy');
        button.querySelector('.loading__spinner')?.classList.add('hidden');
      }
    }

    announce(message, state) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.hidden = false;
      if (state) this.status.dataset.state = state;
    }

    async fetchCart() {
      const response = await fetch(`${routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
      });
      const cart = await this.parseJson(response);
      if (!response.ok || !Array.isArray(cart?.items)) throw new Error('Cart could not be verified.');
      return cart;
    }

    async parseJson(response) {
      try {
        return await response.json();
      } catch (error) {
        return null;
      }
    }
  }

  class SaCartPromotionManager {
    constructor() {
      this.controllers = new Set();
      this.cartUpdateUnsubscriber = null;
      this.initialize(document);
      this.subscribeToCartUpdates();

      document.addEventListener('shopify:section:load', event => this.initialize(event.target));
      document.addEventListener('shopify:section:unload', event => this.destroyWithin(event.target));
    }

    initialize(root) {
      const roots = [];
      if (root instanceof Element && root.matches(ROOT_SELECTOR)) roots.push(root);
      root.querySelectorAll?.(ROOT_SELECTOR).forEach(element => roots.push(element));

      roots.forEach(element => {
        if (element.saCartPromotionController) return;
        const controller = new SaCartPromotionController(element);
        element.saCartPromotionController = controller;
        this.controllers.add(controller);
      });
    }

    destroyWithin(root) {
      this.controllers.forEach(controller => {
        if (controller.root === root || root.contains?.(controller.root)) {
          controller.destroy();
          delete controller.root.saCartPromotionController;
          this.controllers.delete(controller);
        }
      });
    }

    subscribeToCartUpdates() {
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
        this.controllers.forEach(controller => controller.handleSuccessfulAdd(event));
      });
    }
  }

  window.saCartPromotionUpsell = new SaCartPromotionManager();
})();
